import React, { useEffect, useState } from "react";
import { Card } from "primereact/card";
import { ProgressBar } from "primereact/progressbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { getChildSummary } from "../../api/parentDashboardApi";
import { useCurrency, formatMoney } from "../../common/utils/currency";

const ChildOverviewTab = ({ child }) => {
  const currency = useCurrency();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!child?.id) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getChildSummary(child.id);
        if (data?.success) setSummary(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [child?.id]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (!summary) return <p>No data available</p>;

  return (
    <div className="grid">
      <div className="col-12 md:col-6 lg:col-3">
        <Card className="kpi-card">
          <div className="flex justify-content-between align-items-start">
            <div>
              <div className="text-sm opacity-70">Attendance (30d)</div>
              <div className="text-3xl font-bold mt-2">{summary.attendance.percent}%</div>
              <div className="text-xs opacity-60 mt-1">
                {summary.attendance.presentDays}/{summary.attendance.totalDays} days
              </div>
            </div>
            <i className="pi pi-check-circle text-primary" style={{ fontSize: "2rem" }}></i>
          </div>
          <ProgressBar value={summary.attendance.percent} showValue={false} className="mt-3" />
        </Card>
      </div>

      <div className="col-12 md:col-6 lg:col-3">
        <Card className="kpi-card">
          <div className="flex justify-content-between align-items-start">
            <div>
              <div className="text-sm opacity-70">Pending Assignments</div>
              <div className="text-3xl font-bold mt-2">{summary.pendingAssignments}</div>
              <div className="text-xs opacity-60 mt-1">Not yet submitted</div>
            </div>
            <i className="pi pi-file-edit text-orange-500" style={{ fontSize: "2rem" }}></i>
          </div>
        </Card>
      </div>

      <div className="col-12 md:col-6 lg:col-3">
        <Card className="kpi-card">
          <div className="flex justify-content-between align-items-start">
            <div>
              <div className="text-sm opacity-70">Unpaid Fees</div>
              <div className="text-3xl font-bold mt-2">{summary.unpaidVouchers}</div>
              <div className="text-xs opacity-60 mt-1">
                Total: {formatMoney(summary.unpaidAmount, currency)}
              </div>
            </div>
            <i className="pi pi-wallet text-red-500" style={{ fontSize: "2rem" }}></i>
          </div>
        </Card>
      </div>

      <div className="col-12 md:col-6 lg:col-3">
        <Card className="kpi-card">
          <div className="flex justify-content-between align-items-start">
            <div>
              <div className="text-sm opacity-70">Recent Results</div>
              <div className="text-3xl font-bold mt-2">{summary.recentResults?.length || 0}</div>
              <div className="text-xs opacity-60 mt-1">Published exam results</div>
            </div>
            <i className="pi pi-chart-line text-green-500" style={{ fontSize: "2rem" }}></i>
          </div>
        </Card>
      </div>

      {summary.recentResults?.length > 0 && (
        <div className="col-12">
          <Card title="Recent Exam Results">
            <ul className="list-none p-0 m-0">
              {summary.recentResults.map((r) => (
                <li
                  key={r.id}
                  className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border"
                >
                  <div>
                    <div className="font-medium">{r.exam?.name || "Exam"}</div>
                    <div className="text-sm opacity-70">
                      {r.exam?.exam_date ? new Date(r.exam.exam_date).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <div className="flex align-items-center gap-2">
                    <Tag value={`${r.marks_obtained || 0} / ${r.exam?.total_marks || "?"}`} />
                    {r.grade_letter && <Tag severity="success" value={r.grade_letter} />}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ChildOverviewTab;
